import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Driver } from "../../models";

interface DriverSelectionStepProps {
  drivers: Driver[];
  selectedDriverId: string;
  onDriverSelect: (driverId: string) => void;
}

export const DriverSelectionStep: React.FC<DriverSelectionStepProps> = ({
  drivers,
  selectedDriverId,
  onDriverSelect,
}) => {
  const { t } = useTranslation(["orders"]);
  const [searchText, setSearchText] = useState<string>("");
  const [selectionError, setSelectionError] = useState(false);
  const [searchResults, setSearchResults] = useState<Driver[]>(drivers);

  // Filter drivers based on search text
  useEffect(() => {
    if (searchText.length > 0) {
      const filtered = drivers.filter((driver) =>
        (driver.firstName + " " + driver.lastName)
          .toLowerCase()
          .includes(searchText.toLowerCase()),
      );
      setSearchResults(filtered);
    } else {
      setSearchResults(drivers);
    }
  }, [searchText, drivers]);

  const handleSelectDriver = (driver: Driver) => {
    if (!driver.companyId) {
      setSelectionError(true);
      return;
    }
    setSelectionError(false);

    // Toggle functionality: if driver is already selected, deselect it
    if (selectedDriverId === driver.email) {
      onDriverSelect("");
    } else {
      onDriverSelect(driver.email);
    }
  };

  return (
    <div className="space-y-4">
      {selectionError && (
        <p role="alert" className="text-sm text-danger">
          {t("create.driver_selection.missing_companyId")}
        </p>
      )}
      <input
        type="text"
        className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm bg-surface-input text-text placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
        placeholder={t("create.driver_selection.search_placeholder")}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        autoComplete="off"
      />

      <div className="max-h-64 overflow-y-auto border border-border rounded-lg bg-surface-input">
        {searchResults.length === 0 ? (
          <div className="p-4 text-center text-muted">
            {searchText
              ? t("orders:no_drivers_found")
              : t("orders:no_drivers_available")}
          </div>
        ) : (
          searchResults.map((driver: Driver) => (
            <div
              key={driver.id}
              onClick={() => handleSelectDriver(driver)}
              className={`p-3 border-b border-gray-100 dark:border-gray-600 last:border-b-0 cursor-pointer transition-colors ${
                selectedDriverId === driver.email
                  ? "bg-brand-muted dark:bg-brand-muted/20 border-brand-muted dark:border-brand"
                  : "hover:bg-gray-50 dark:hover:bg-gray-600"
              }`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedDriverId === driver.email
                      ? "border-brand bg-brand"
                      : "border-gray-300 dark:border-gray-500"
                  }`}
                >
                  {selectedDriverId === driver.email && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <span className="text-sm font-medium text-text">
                  {`${driver.firstName} ${driver.lastName}`}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
